import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  cashCodeToken,
  cashCodeWords,
  sendCashCode,
  type CashCodeSent,
} from "../api/cashCode";
import type { CallerIdentity } from "../api/gateway";
import {
  CodeChannelChoice,
  codeChannels,
  type CodeChannel,
} from "./CodeChannelChoice";
import { useResendTimer } from "./useResendTimer";

/**
 * Confirm cash with a code sent to the login recording it (owner 2026-11-01).
 * Opens when the operator records cash. Where the code is not switched on the
 * server says so and this answers at once with no code. A Resend waits, as on
 * every send-code screen (owner 2026-10-23).
 */
export function CashCodeDialog(props: {
  open: boolean;
  identity: CallerIdentity;
  ns: string;
  account: string;
  amount: number;
  saleRef: string;
  onConfirm: (cashCode: string | undefined) => void;
  onCancel: () => void;
}) {
  const [channel, setChannel] = useState<CodeChannel>("sms");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState<Extract<
    CashCodeSent,
    { needed: true }
  > | null>(null);
  const timer = useResendTimer();
  const { open, identity, ns, account, amount, saleRef, onConfirm } = props;

  const send = useMutation({
    mutationFn: () =>
      sendCashCode(identity, ns, {
        account,
        amount,
        ref: saleRef,
        channels: codeChannels(channel),
      }),
    onSuccess: (r) => {
      if (!r.needed) {
        onConfirm(undefined);
        return;
      }
      setSent(r);
      setCode("");
      timer.start(r.resendIn);
    },
  });

  // Ask once on open whether a code is needed at all: most namespaces say no.
  const [asked, setAsked] = useState(false);
  useEffect(() => {
    if (open && !asked) {
      setAsked(true);
      send.mutate();
    }
    if (!open && asked) {
      setAsked(false);
      setSent(null);
      setCode("");
      send.reset();
    }
  }, [open, asked, send]);

  const digits = code.trim();
  const ready = sent !== null && /^[0-9]{6}$/.test(digits);
  const where = sent
    ? [
        sent.sent.includes("sms") ? "by SMS to your mobile" : "",
        sent.email ? `by email to ${sent.email}` : "",
      ]
        .filter(Boolean)
        .join(" and ")
    : "";

  return (
    <Dialog open={open} onClose={props.onCancel} fullWidth maxWidth="xs">
      <DialogTitle>Confirm the cash</DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
      >
        <Typography variant="body2">
          ₹{amount.toLocaleString("en-IN")} in cash. A code goes to you, the
          person recording it.
        </Typography>
        <CodeChannelChoice
          value={channel}
          onChange={setChannel}
          disabled={send.isPending}
        />
        {sent && (
          <Alert severity="info">
            Code sent {where}. It is valid for 10 minutes.
          </Alert>
        )}
        {send.error && (
          <Alert severity="error">
            {cashCodeWords(
              send.error instanceof Error
                ? send.error.message
                : String(send.error),
            )}
          </Alert>
        )}
        {sent && (
          <TextField
            size="small"
            label="Code"
            autoFocus
            inputProps={{ inputMode: "numeric", maxLength: 6 }}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ""))}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onCancel}>Cancel</Button>
        <Button
          disabled={send.isPending || (sent !== null && timer.left > 0)}
          onClick={() => send.mutate()}
        >
          {send.isPending
            ? "Sending…"
            : sent
              ? timer.left > 0
                ? `Resend in ${timer.left}s`
                : "Resend code"
              : "Send code"}
        </Button>
        <Button
          variant="contained"
          disabled={!ready}
          onClick={() =>
            sent && onConfirm(cashCodeToken(sent.requestId, digits))
          }
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
}
